import gsap from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

gsap.registerPlugin(MotionPathPlugin);

export class DotsAnimation {
  constructor() {
    this.isInitialized = false;
    this.dots = new Map();
    this.prefersReducedMotion = false;
  }

  init() {
    if (this.isInitialized) return;

    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.initDots();
    this.isInitialized = true;
  }

  initDots() {
    document.querySelectorAll('[data-dots]').forEach((container, index) => {
      if (this.dots.has(container)) {
        this.destroyDot(container);
      }

      const dot = new DotInstance(container, index, {
        prefersReducedMotion: this.prefersReducedMotion
      });
      this.dots.set(container, dot);
    });
  }

  destroyDot(container) {
    const dot = this.dots.get(container);
    if (dot) {
      dot.destroy();
      this.dots.delete(container);
    }
  }

  destroy() {
    this.dots.forEach((dot, container) => {
      this.destroyDot(container);
    });

    this.isInitialized = false;
  }
}

class DotInstance {
  constructor(container, index, options = {}) {
    this.container = container;
    this.index = index;
    this.options = options;

    this.timelines = [];
    this.svgElements = [];
    this.circles = this.getDefaultCircles();
    this.colors = ['#7D00FF', '#7D00FF'];
    this.numDots = 5;

    this.init();
  }

  init() {
    if (this.options.prefersReducedMotion) return;

    // Get configuration from data attributes
    const numDotsAttr = this.container.getAttribute('data-dots-count');
    if (numDotsAttr) {
      this.numDots = parseInt(numDotsAttr, 10);
    }

    const colorsAttr = this.container.getAttribute('data-dots-colors');
    if (colorsAttr) {
      this.colors = colorsAttr.split(',').map(c => c.trim());
    }

    const circlesAttr = this.container.getAttribute('data-dots-circles');
    if (circlesAttr) {
      try {
        this.circles = JSON.parse(circlesAttr);
      } catch (e) {
        console.warn('Invalid circles data, using defaults');
      }
    }

    this.setupDots();
  }

  getDefaultCircles() {
    return [
      { cx: 690.183, cy: 690.183, r: 513.183 },
      { cx: 689.496, cy: 689.496, r: 466.496 },
      { cx: 690.421, cy: 690.421, r: 428.421 },
      { cx: 690.406, cy: 690.406, r: 392.406 },
      { cx: 689.896, cy: 689.896, r: 356.896 },
      { cx: 689.954, cy: 689.954, r: 316.954 },
      { cx: 690.305, cy: 690.305, r: 280.305 },
      { cx: 690.293, cy: 690.293, r: 242.293 },
      { cx: 689.958, cy: 689.958, r: 208.958 },
      { cx: 690.345, cy: 690.345, r: 167.345 },
      { cx: 689.94, cy: 689.94, r: 129.94 },
      { cx: 690.259, cy: 690.259, r: 95.2588 },
      { cx: 689.758, cy: 689.758, r: 65.7582 },
      { cx: 690.089, cy: 690.089, r: 40.0886 },
      { cx: 690.178, cy: 690.178, r: 14.178 },
      { cx: 689.643, cy: 689.643, r: 587.643 },
      { cx: 690.372, cy: 690.372, r: 621.372 },
      { cx: 689.846, cy: 689.846, r: 654.846 },
      { cx: 689.944, cy: 689.944, r: 689.944 },
      { cx: 689.53, cy: 689.53, r: 552.53 }
    ];
  }

  setupDots() {
    let svgContainer = this.container.querySelector('svg');
    if (!svgContainer) {
      console.warn('No SVG element found in container');
      return;
    }

    this.revealCircles(svgContainer).then(() => {
      this.createDotElements(svgContainer);
    });
  }

  revealCircles(svgContainer) {
    return new Promise((resolve) => {
      const circleElements = Array.from(svgContainer.querySelectorAll('circle'));

      if (circleElements.length === 0) {
        resolve();
        return;
      }

      // Sort circles by radius (smallest first)
      const circlesWithRadius = circleElements.map(circle => ({
        element: circle,
        radius: parseFloat(circle.getAttribute('r')) || 0
      })).sort((a, b) => a.radius - b.radius);

      // Set initial state - scaled down from center
      circlesWithRadius.forEach(({ element }) => {
        const cx = parseFloat(element.getAttribute('cx')) || 0;
        const cy = parseFloat(element.getAttribute('cy')) || 0;

        gsap.set(element, {
          transformOrigin: `${cx}px ${cy}px`,
          scale: 0,
          opacity: 0
        });
      });

      const revealTl = gsap.timeline({
        onComplete: resolve
      });

      circlesWithRadius.forEach(({ element }, index) => {
        revealTl.to(element, {
          scale: 1,
          opacity: 1,
          duration: 1.6,
          ease: "expo.out"
        }, index * 0.04);
      });

      this.timelines.push(revealTl);
    });
  }

  createDotElements(svgContainer) {
    let dotsGroup = svgContainer.querySelector('[data-dots-container]');
    if (!dotsGroup) {
      dotsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      dotsGroup.setAttribute('data-dots-container', '');
      svgContainer.appendChild(dotsGroup);
    }

    const selectedCircles = this.selectRandomCircles(this.numDots);

    selectedCircles.forEach((circle, i) => {
      this.createDot(dotsGroup, circle, i);
    });
  }

  selectRandomCircles(count) {
    const bigCircles = this.circles
      .map((circle, index) => ({ ...circle, originalIndex: index }))
      .filter(circle => circle.r > 300);

    const selected = [];
    const usedIndices = new Set();

    while (selected.length < count && selected.length < bigCircles.length) {
      const randomIndex = Math.floor(Math.random() * bigCircles.length);
      if (!usedIndices.has(randomIndex)) {
        usedIndices.add(randomIndex);
        selected.push({ ...bigCircles[randomIndex] });
      }
    }

    return selected;
  }

  createDot(container, circle, i) {
    const dotId = `dot-${this.index}-${i}`;
    const color = this.colors[i % this.colors.length];

    const cx = circle.cx;
    const cy = circle.cy;
    const r = circle.r;
    const pathD = `M ${cx}, ${cy - r} A ${r} ${r} 0 0 1 ${cx}, ${cy + r} A ${r} ${r} 0 0 1 ${cx}, ${cy - r}`;

    const dotGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    dotGroup.id = dotId;
    dotGroup.setAttribute('data-dot-group', '');

    // Create clipPath for trail
    const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
    clipPath.id = `trail-${dotId}`;
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.classList.add('mask', `mask-${dotId}`);
    rect.setAttribute('width', '0');
    rect.setAttribute('height', '25');
    rect.setAttribute('y', '0');
    clipPath.appendChild(rect);

    // Create hidden path for motion
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.id = `orbit-${dotId}`;
    path.setAttribute('d', pathD);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'transparent');

    // Create trail path
    const trailPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    trailPath.classList.add('trail', `trail-${dotId}`);
    trailPath.setAttribute('d', pathD);
    trailPath.setAttribute('fill', 'none');
    trailPath.setAttribute('stroke', color);
    trailPath.setAttribute('stroke-width', '3');
    trailPath.setAttribute('clip-path', `url(#trail-${dotId})`);

    // Create dot circle
    const dotCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dotCircle.classList.add('dot', `dot-${dotId}`);
    dotCircle.setAttribute('r', '3');
    dotCircle.setAttribute('fill', color);

    // Append elements
    dotGroup.appendChild(clipPath);
    dotGroup.appendChild(path);
    dotGroup.appendChild(trailPath);
    dotGroup.appendChild(dotCircle);
    container.appendChild(dotGroup);

    // Store references for cleanup
    this.svgElements.push(dotGroup);

    // Create animation
    this.createAnimation(dotId, i);
  }

  createAnimation(dotId, index) {
    const dur = 4 + Math.random() * 6; // Random duration between 4-10s
    const trail = 30 + Math.random() * 40; // Random trail length
    const startPos = Math.random() * -0.3; // Random start position
    const ease = "power1.inOut";

    gsap.set(`#${dotId}`, {
      // scale: 0,
      opacity: 0,
      transformOrigin: "center center"
    });

    const revealTl = gsap.timeline({
      delay: index * 0.1 // Stagger dot reveals
    });

    revealTl.to(`#${dotId}`, {
      opacity: 1,
      // scale: 1,
      duration: 0.6,
      ease: "back.out(1.4)"
    });

    const orbitTl = gsap.timeline({
      delay: (index * 0.1) + 0.6
    });

    // Animate dot
    orbitTl.to(`.dot-${dotId}`, {
      motionPath: {
        path: `#orbit-${dotId}`,
        autoRotate: true,
        start: startPos,
        end: 1 + startPos
      },
      duration: dur,
      ease: ease,
      immediateRender: true,
    }, 0);

    // Animate mask
    orbitTl.to(`.mask-${dotId}`, {
      motionPath: {
        path: `#orbit-${dotId}`,
        autoRotate: true,
        start: startPos,
        end: 1 + startPos
      },
      scaleX: 1,
      duration: dur,
      ease: ease,
      immediateRender: true,
    }, 0);

    // Trail grow
    orbitTl.to(`.mask-${dotId}`, {
      width: trail,
      duration: dur / 2,
      ease: ease,
    }, 0);

    // Trail shrink
    orbitTl.to(`.mask-${dotId}`, {
      width: 0,
      duration: dur / 2,
      ease: ease,
    }, dur / 2);

    // Mirror effect
    orbitTl.fromTo(`.mask-${dotId}`, {
      scaleX: -1
    }, {
      scaleX: -1,
      duration: dur
    }, 0);

    orbitTl.yoyo(false).repeat(-1).repeatDelay(0.25 + Math.random() * 0.5);
    orbitTl.play();

    this.timelines.push(revealTl);
    this.timelines.push(orbitTl);
  }

  destroy() {
    // Kill all timelines
    this.timelines.forEach(tl => {
      if (tl) {
        tl.kill();
      }
    });
    this.timelines = [];

    // Remove SVG elements
    this.svgElements.forEach(element => {
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    this.svgElements = [];
  }
}